"use client";

import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useContext, useEffect, useState } from "react";
import { BREAKPOINTS, DEFAULT_CURRENCY } from "@lib/constants";
import customersManager from "@lib/data/managers/shared/customers-manager";
import { useNotifications } from "./notifications-context";
import { useTranslations } from "next-intl";
import APP_CONFIGURATION from "@lib/app.config";
import { NotificationType } from "types/shared/notification-types";
import { Gender } from "types/shared/gender";
import ERROR_CODES from "@lib/error-codes";
import useMuscleRouter from "@lib/hooks/use-muscle-router";
import assetsStotyblokService from "@lib/storyblok/services/assets-service";
import { useModalMessages } from "./modal-message-context";
import { useIsLoadingResources } from "./loading-resources-context";
import mixpanelService from "@lib/mixpanel/mixpanel.service";
import { MIXPANEL_EVENTS } from "@lib/mixpanel/mixpanel.event-list";
import { usePathname } from "next/navigation";
import { v4 as uuid } from 'uuid';
import { useErrorPage } from "./errors-context";
import { encodeObjectToQueryString } from "@lib/util/object-ops";
import { permissionsManager } from "@lib/permissions/permissions-manager";
import { useMediaQuery } from "react-responsive";
export const notStartedProvider = "not_started";
const CustomerContext = /*#__PURE__*/createContext(null);
const customerDefaultValues = {
  customerIsLogged: false,
  customerIsValidated: false,
  isLoadingCustomer: true,
  isRefreshingData: false,
  customerId: 0,
  customerName: "",
  customerLastName: "",
  customerEmail: "",
  customerProfilePic: null,
  customerBirthDate: "",
  customerGender: Gender.M,
  customerMaritalStatus: "MA",
  customerCreditCardType: "",
  customerPhoneNumber: "",
  customerCountry: "",
  customerState: "",
  customerCity: "",
  customerPostalCode: "",
  customerFirstLine: "",
  customerAvailablePoints: 0,
  customerCurrency: DEFAULT_CURRENCY,
  customerDefaultCard: null,
  customerSavedCreditCards: [],
  customer_segment_id: 0,
  customer_segment_name: "",
  customerProvider: 'reseted',
  showUtilityModal: false,
  identification_number: "",
  bank_uniq_id: "",
  showedCommunicationNotification: false
};
export const CustomerProvider = _ref => {
  let {
    children
  } = _ref;
  const {
    notify
  } = useNotifications();
  const router = useMuscleRouter();
  const t = useTranslations("Shared.Auth");
  const tShared = useTranslations("Shared");
  const {
    showModalMessage
  } = useModalMessages();
  const {
    setIsLoadingPage
  } = useIsLoadingResources();
  const optionId = router.getQueryParam('optionId');
  const {
    renderErrorPage
  } = useErrorPage();
  const allowUnauthenticatedCustomer = permissionsManager.allowUnauthenticatedUsers();

  // Flags
  const {
    0: isLoadingCustomer,
    1: setIsLoadingCustomer
  } = useState(customerDefaultValues.isLoadingCustomer);
  const {
    0: isRefreshingData,
    1: setIsRefreshingData
  } = useState(customerDefaultValues.isRefreshingData);
  const {
    0: customerIsLogged,
    1: setCustomerIsLogged
  } = useState(customerDefaultValues.customerIsLogged);
  const {
    0: customerIsValidated,
    1: setCustomerIsValidated
  } = useState(customerDefaultValues.customerIsValidated);

  // Customer personal info states
  const {
    0: customerId,
    1: setCustomerId
  } = useState(customerDefaultValues.customerId);
  const {
    0: customerName,
    1: setCustomerName
  } = useState(customerDefaultValues.customerName);
  const {
    0: customerLastName,
    1: setCustomerLastName
  } = useState(customerDefaultValues.customerLastName);
  const {
    0: customerEmail,
    1: setCustomerEmail
  } = useState(customerDefaultValues.customerEmail);
  const {
    0: customerProfilePic,
    1: setCustomerProfilePic
  } = useState(customerDefaultValues.customerProfilePic);
  const {
    0: customerBirthDate,
    1: setCustomerBirthDate
  } = useState(customerDefaultValues.customerBirthDate);
  const {
    0: customerGender,
    1: setCustomerGender
  } = useState(customerDefaultValues.customerGender);
  const {
    0: customerMaritalStatus,
    1: setCustomerMaritalStatus
  } = useState(customerDefaultValues.customerMaritalStatus);
  const {
    0: customerCreditCardType,
    1: setCustomerCreditCardType
  } = useState(customerDefaultValues.customerCreditCardType);
  const {
    0: customerPhoneNumber,
    1: setCustomerPhoneNumber
  } = useState(customerDefaultValues.customerPhoneNumber);
  const {
    0: customerCountry,
    1: setCustomerCountry
  } = useState(customerDefaultValues.customerCountry);
  const {
    0: customerState,
    1: setCustomerState
  } = useState(customerDefaultValues.customerState);
  const {
    0: customerCity,
    1: setCustomerCity
  } = useState(customerDefaultValues.customerCity);
  const {
    0: customerPostalCode,
    1: setCustomerPostalCode
  } = useState(customerDefaultValues.customerPostalCode);
  const {
    0: customerFirstLine,
    1: setCustomerFirstLine
  } = useState(customerDefaultValues.customerFirstLine);
  const {
    0: customerAvailablePoints,
    1: setCustomerAvailablePoints
  } = useState(customerDefaultValues.customerAvailablePoints);
  const {
    0: customerCurrency,
    1: setCustomerCurrency
  } = useState(customerDefaultValues.customerCurrency);
  const {
    0: customerDefaultCard,
    1: setCustomerDefaultCard
  } = useState(customerDefaultValues.customerDefaultCard);
  const {
    0: customerSavedCreditCards,
    1: setCustomerSavedCreditCards
  } = useState(customerDefaultValues.customerSavedCreditCards);
  const {
    0: customerSegmentId,
    1: setCustomerSegmentId
  } = useState(customerDefaultValues.customer_segment_id);
  const {
    0: customerSegmentName,
    1: setCustomerSegmentName
  } = useState(customerDefaultValues.customer_segment_name);
  const {
    0: customerProvider,
    1: setCustomerProvider
  } = useState(notStartedProvider);
  const {
    0: customerIdentificationNumber,
    1: setCustomerIdentificationNumber
  } = useState(customerDefaultValues.identification_number);
  const {
    0: customerBankUniqueId,
    1: setCustomerBankUniqueId
  } = useState(customerDefaultValues.bank_uniq_id);
  const isMobile = useMediaQuery({
    query: "(max-width: ".concat(BREAKPOINTS.md, "px)")
  });
  const {
    0: isLoadingCustomerSavedCreditCards,
    1: setIsLoadingCustomerSavedCreditCards
  } = useState(false);
  const {
    0: refreshingPoints,
    1: setRefreshingPoints
  } = useState(false);
  const {
    0: isSessionProviderActive,
    1: setIsSessionProviderActive
  } = useState(false);
  const {
    0: lastUserSearch,
    1: setLastUserSearch
  } = useState();
  const {
    0: loadingLogin,
    1: setLoadingLogin
  } = useState(false);
  const {
    0: loadingProviderValidation,
    1: setLoadingProviderValidation
  } = useState(false);
  const path = usePathname();
  const {
    0: isInitializingWithUrlRouteToken,
    1: setIsInitializingWithUrlRouteToken
  } = useState(true);
  const {
    0: showUtilityModal,
    1: setShowUtilityModal
  } = useState(customerDefaultValues.showUtilityModal);
  const {
    0: showedCommunicationNotification,
    1: setShowedCommunicationNotification
  } = useState(customerDefaultValues.showedCommunicationNotification);
  let initialized = false;
  useEffect(() => {
    if (initialized) return;
    initializeCustomerContext();
    initialized = true;
  }, []);
  useEffect(() => {
    if (customerIsLogged) {
      getAndSetSavedCreditCards();
      localStorage.setItem("landing-check", "false");
      notifyWelcomeMessage();
      setIsInitializingWithUrlRouteToken(false);
    }
  }, [customerIsLogged]);
  useEffect(() => {
    if (path.includes('payment') || !router.isHome()) {
      return;
    }
    if (optionId || !customerIsLogged) {
      return;
    }
    if (loadingLogin || loadingProviderValidation) {
      return;
    }
  }, [loadingLogin, loadingProviderValidation, customerIsLogged]);
  const notifyWelcomeMessage = () => {
    const minPointsToAllowPurchase = APP_CONFIGURATION.config.minPointsToAllowPurchase || 0;
    const {
      includeDiscoverTheBestOptionsText,
      notificationStyle
    } = APP_CONFIGURATION.config.welcomeMessageConfig;
    const minPointsToAllowPurchaseReminder = minPointsToAllowPurchase > 0 ? __jsx(React.Fragment, null, "\n", t('REMEMBER_YOU_NEED_AT_LEAST'), "\n", __jsx("span", {
      className: "tx-pmd-semiBold"
    }, t("POINTS_TO_PURCHASE", {
      minPointsToAllowPurchase: Number(minPointsToAllowPurchase).toLocaleString(),
      pointsName: APP_CONFIGURATION.config.pointsCurrency
    }))) : null;
    const notificationMessage = customerIsValidated && includeDiscoverTheBestOptionsText ? __jsx("p", {
      className: "tx-pmd-regular",
      style: {
        whiteSpace: 'pre-line'
      }
    }, t("WELCOME"), customerName && ", ".concat(customerName, "!"), " ", t("DISCOVER_THE_BEST"), " ", __jsx("span", {
      className: "tx-pmd-regular"
    }, customerAvailablePoints.toLocaleString(), " ", APP_CONFIGURATION.config.pointsCurrency), " ", minPointsToAllowPurchaseReminder) : __jsx("p", {
      className: "tx-pmd-regular",
      style: {
        whiteSpace: 'pre-line'
      }
    }, t("WELCOME"), customerName && ", ".concat(customerName, "!"), " ", minPointsToAllowPurchaseReminder);
    notify(notificationMessage, notificationStyle);
  };
  const renderError = () => {
    if (APP_CONFIGURATION.config.render_error_page_on_auth_validate && !isLoadingCustomer) {
      logoutFromFrontend(true);
      renderErrorPage({
        code: "no_session_found",
        subtitle: tShared("WHAT_CAN_I_DO"),
        title: tShared("Errors.PAGE_ERRORS.NO_SESSION_FOUND.TITLE"),
        message: tShared.rich("Errors.PAGE_ERRORS.NO_SESSION_FOUND.MESSAGE", {
          bankEntity: APP_CONFIGURATION.config.appName,
          supportPhoneNumber: APP_CONFIGURATION.config.contactInformation.phoneNumber,
          strong: chunks => __jsx("span", {
            className: "tx-plg-medium"
          }, chunks)
        }),
        addButton: false
      });
    }
  };
  const initializeCustomerContext = async () => {
    const customerAccessToken = localStorage.getItem("access-token");
    const customerJwtToken = router.getQueryParam("auth_token");
    const loginMode = process.env.NEXT_PUBLIC_LOGIN_MODE || 'none';
    if (customerAccessToken && !customerJwtToken) {
      await setAuthenticatedCustomer(false, undefined);
      checkActiveProviderSession();
    } else {
      if (loginMode === "token_param") {
        setIsInitializingWithUrlRouteToken(true);
        localStorage.removeItem('access-token');
        localStorage.removeItem("guest-access-token");
        resetCustomerStates();
        await initializeCustomerContextUrlParamWithToken();
      } else {
        if (allowUnauthenticatedCustomer) {
          await setGuestCustomer();
        }
      }
      setIsLoadingCustomer(false);
    }
  };
  const initializeCustomerContextUrlParamWithToken = async () => {
    const customerAccessToken = router.getQueryParam("auth_token");
    if (process.env.NEXT_PUBLIC_SHOW_LANDING_MAINTENANCE === "true") {
      return;
    }
    if (customerAccessToken) {
      setTimeout(async () => {
        let guestToken = await setGuestCustomer();
        let loginCustomer = {
          jwt: customerAccessToken,
          guest_user_token: guestToken
        };
        localStorage.setItem("guest-access-token", guestToken);
        let response = await login(loginCustomer);
        setIsInitializingWithUrlRouteToken(false);
        if (response.status) {
          const openParam = router.getQueryParam("open");
          if (openParam === "bookings") {
            router.push("/account?open=bookings");
          }
        }
      }, 2000);
    } else {
      setTimeout(() => {
        renderErrorPage({
          code: "403",
          title: tShared("Errors.PAGE_ERRORS.NO_TOKEN_FOUND.TITLE"),
          subtitle: tShared("WHAT_CAN_I_DO"),
          message: tShared.rich("Errors.PAGE_ERRORS.NO_TOKEN_FOUND.MESSAGE", {
            bankEntity: APP_CONFIGURATION.config.appName,
            supportPhoneNumber: APP_CONFIGURATION.config.contactInformation.phoneNumber,
            strong: chunks => __jsx("span", {
              className: "tx-plg-medium"
            }, chunks)
          }),
          addButton: false
        });
      }, 3000);
    }
  };
  const checkActiveProviderSession = async () => {
    const currentAccessToken = localStorage.getItem('access-token') || undefined;
    try {
      setRefreshingPoints(true);
      setLoadingProviderValidation(true);
      const response = await customersManager.validateProviderSession();
      setRefreshingPoints(false);
      setLoadingProviderValidation(false);
      if (!(response !== null && response !== void 0 && response.code)) {
        // false - no active session
        setCustomerIsValidated(true);
        setIsSessionProviderActive(true);
        return true;
        //
      } else {
        localStorage.setItem('expiration-state', 'expired');
        if (currentAccessToken) {
          localStorage.setItem('guest-access-token', currentAccessToken);
        }
        setIsSessionProviderActive(false);
        renderError();
        return false;
      }
    } catch (e) {
      localStorage.setItem('expiration-state', 'expired');
      if (currentAccessToken) {
        localStorage.setItem('guest-access-token', currentAccessToken);
      }
      console.error('Error validating provider session:', e);
      setLoadingProviderValidation(false);
      setIsSessionProviderActive(false);
      renderError();
      return false;
    }
  };

  /**
   * Set the authenticated customer
   * @param onlyRefresh - If true, only refresh the customer data
   * @param data - The customer data to set
   * @returns - Force redirect to flow initial page
   */
  const setAuthenticatedCustomer = async (onlyRefresh, data) => {
    var _data$redirect_to_flo;
    if (!onlyRefresh) setIsLoadingCustomer(true);
    setIsRefreshingData(true);
    setIsLoadingPage(true);
    setLoadingLogin(true);
    const loggedCustomer = data || (await customersManager.getLoggedCustomer());
    setLoadingLogin(false);
    const redirectToFlowInitialPage = (_data$redirect_to_flo = data === null || data === void 0 ? void 0 : data.redirect_to_flow_initial_page) !== null && _data$redirect_to_flo !== void 0 ? _data$redirect_to_flo : false;
    if (loggedCustomer !== null) {
      var _loggedCustomer$acces;
      localStorage.setItem("currency", loggedCustomer.currency);
      localStorage.setItem("access-token", (_loggedCustomer$acces = loggedCustomer.access_token) !== null && _loggedCustomer$acces !== void 0 ? _loggedCustomer$acces : "");
      localStorage.setItem('expiration-state', 'valid');
      updateCustomerStates(loggedCustomer);
      setCustomerIsLogged(true);
    } else {
      logoutFromFrontend();
    }
    if (!onlyRefresh) setIsLoadingCustomer(false);
    setIsRefreshingData(false);
    setIsLoadingPage(false);
    return redirectToFlowInitialPage !== null && redirectToFlowInitialPage !== void 0 ? redirectToFlowInitialPage : false;
  };
  const setGuestCustomer = async origin => {
    var _guestResponse$curren, _guestResponse$guest_;
    setIsLoadingCustomer(true);

    // Gets the guest token
    const guestResponse = await customersManager.getGuestCustomer();
    // If error getting token, show notification
    if (guestResponse.error) {
      notify(t("ERROR_GETTING_GUEST_TOKEN"), NotificationType.ERROR);
    } else {
      // If no errors, set new token
      localStorage.setItem("guest-access-token", guestResponse.access_token);
      // notify(t('GUEST_ALERT'), NotificationType.SUCCESS)
    }
    updateCustomerStates({
      country: guestResponse === null || guestResponse === void 0 ? void 0 : guestResponse.country,
      currency: guestResponse === null || guestResponse === void 0 ? void 0 : guestResponse.currency
    });
    localStorage.setItem("currency", (_guestResponse$curren = guestResponse === null || guestResponse === void 0 ? void 0 : guestResponse.currency) !== null && _guestResponse$curren !== void 0 ? _guestResponse$curren : DEFAULT_CURRENCY);
    localStorage.setItem("guest_id", (_guestResponse$guest_ = guestResponse === null || guestResponse === void 0 ? void 0 : guestResponse.guest_id) !== null && _guestResponse$guest_ !== void 0 ? _guestResponse$guest_ : uuid());
    setCustomerIsLogged(false);
    setIsLoadingCustomer(false);
    return guestResponse.access_token;
  };
  const login = async (loginCustomer, provider_info) => {
    var _localStorage$getItem;
    setLoadingLogin(true);

    // Guest access token could be present
    const guestAccessToken = (_localStorage$getItem = localStorage.getItem("guest-access-token")) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : undefined;
    try {
      const loggedInCustomerInfo = !provider_info ? await customersManager.login(loginCustomer) : await customersManager.thirdPartyLogin(provider_info.provider_id, provider_info.provider_token, guestAccessToken);
      setLoadingLogin(false);
      if (loggedInCustomerInfo && "access_token" in loggedInCustomerInfo && typeof loggedInCustomerInfo.access_token === "string") {
        localStorage.removeItem("guest-access-token");
        localStorage.setItem("access-token", loggedInCustomerInfo.access_token);
        localStorage.setItem('expiration-state', 'valid');
        updateCustomerStates(loggedInCustomerInfo);
        localStorage.setItem("currency", loggedInCustomerInfo.currency);
        setCustomerIsLogged(true);
        setIsInitializingWithUrlRouteToken(false);
        checkActiveProviderSession();
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Profile.LOGIN, {
          Name: loggedInCustomerInfo.first_name + " " + loggedInCustomerInfo.last_name,
          Email: loggedInCustomerInfo.email
        }, loggedInCustomerInfo);
        return {
          status: true,
          result: loggedInCustomerInfo
        };
      }
      const errorData = loggedInCustomerInfo;
      if (APP_CONFIGURATION.config.render_error_page_on_auth_validate) {
        if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === "UNKNOWN_ERROR") {
          renderErrorPage({
            code: "403",
            subtitle: tShared("WHAT_CAN_I_DO"),
            message: tShared.rich("Errors.UNKNOWN_ERROR_MESSAGE"),
            addButton: false
          });
        } else {
          renderErrorPage({
            code: "403",
            subtitle: tShared("WHAT_CAN_I_DO"),
            message: (errorData === null || errorData === void 0 ? void 0 : errorData.message) || '',
            addButton: false
          });
        }
      }
      return {
        status: false,
        result: loggedInCustomerInfo
      };
    } catch (e) {
      if (APP_CONFIGURATION.config.render_error_page_on_auth_validate) {
        renderErrorPage({
          code: "403",
          subtitle: tShared("WHAT_CAN_I_DO"),
          message: tShared.rich("Errors.PAGE_ERRORS.403.MESSAGE", {
            bankEntity: APP_CONFIGURATION.config.appName,
            supportPhoneNumber: APP_CONFIGURATION.config.contactInformation.phoneNumber,
            strong: chunks => __jsx("span", {
              className: "tx-plg-medium"
            }, chunks)
          }),
          addButton: false
        });
      }
      return {
        status: false,
        result: {
          error: e.message,
          code: ERROR_CODES.Customers.CUSTOMERS_AUTH_SERVICES_ERROR
        }
      };
    }
  };
  const loginRegisteredUser = async (muscleCustomer, token) => {
    localStorage.setItem("access-token", token);
    localStorage.setItem("currency", muscleCustomer.currency);
    localStorage.setItem('expiration-state', 'valid');
    updateCustomerStates(muscleCustomer);
    mixpanelService.trackEvent(MIXPANEL_EVENTS.Profile.LOGIN_REGISTER_USER, {
      Name: muscleCustomer.first_name + " " + muscleCustomer.last_name,
      Email: muscleCustomer.email
    }, muscleCustomer);
    setCustomerIsLogged(true);
  };
  const logout = async () => {
    const customer = getLoggedCustomer();
    const logoutAction = async () => {
      router.prefetch("/");
      const result = await customersManager.logout();
      if (result) {
        logoutFromFrontend();
        notify(t("LOGGED_OUT_SUCCESSFULLY"), NotificationType.SUCCESS);
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Profile.LOGOUT, {
          Name: (customer === null || customer === void 0 ? void 0 : customer.first_name) + " " + (customer === null || customer === void 0 ? void 0 : customer.last_name),
          Email: customer === null || customer === void 0 ? void 0 : customer.email
        }, customer);
      } else {
        mixpanelService.trackEvent(MIXPANEL_EVENTS.Profile.LOGOUT_ERROR, {
          Name: (customer === null || customer === void 0 ? void 0 : customer.first_name) + " " + (customer === null || customer === void 0 ? void 0 : customer.last_name),
          Email: customer === null || customer === void 0 ? void 0 : customer.email
        }, customer);
      }
    };
    showModalMessage({
      title: t("LOGOUT_CONFIRMATION"),
      message: t("LOGOUT_CONFIRMATION_MESSAGE"),
      actions: [{
        text: t("LOGOUT"),
        onClick: logoutAction,
        buttonVariant: isMobile ? "solidXL" : "solidMd"
      }],
      closeable: true,
      addCancelButton: true
    });
  };
  const logoutFromFrontend = async forceLandingPage => {
    const expiredState = localStorage.getItem('expiration-state');
    if (!expiredState || expiredState && expiredState !== 'expired') {
      router.push("/");
    }
    setIsLoadingPage(false);
    setCustomerIsLogged(false);
    setIsInitializingWithUrlRouteToken(false);
    setTimeout(() => {
      if (allowUnauthenticatedCustomer) {
        setGuestCustomer("logout");
      }
      localStorage.removeItem("access-token");
      resetCustomerStates();
      if (forceLandingPage) {
        router.replace("/");
      }
    }, 500);
    setIsLoadingCustomer(false);
  };
  const updateCustomerStates = loggedCustomer => {
    var _loggedCustomer$oauth, _loggedCustomer$ident, _loggedCustomer$bank_;
    setCustomerId(loggedCustomer.id);
    setCustomerName(loggedCustomer.first_name);
    setCustomerLastName(loggedCustomer.last_name);
    setCustomerAvailablePoints(loggedCustomer.muscle_points);
    setCustomerCurrency(loggedCustomer.currency || DEFAULT_CURRENCY);
    setCustomerProfilePic(loggedCustomer.profile_picture);
    setCustomerEmail(loggedCustomer.email);
    setCustomerBirthDate(loggedCustomer.date_of_birth);
    setCustomerGender(loggedCustomer.gender);
    setCustomerMaritalStatus(loggedCustomer.marital_status);
    setCustomerCreditCardType(loggedCustomer.card_type);
    setCustomerPhoneNumber(loggedCustomer.phone_number);
    setCustomerCountry(loggedCustomer.country);
    setCustomerState(loggedCustomer.state);
    setCustomerCity(loggedCustomer.city);
    setCustomerPostalCode(loggedCustomer.postal_code);
    setCustomerFirstLine(loggedCustomer.first_line);
    setCustomerSegmentId(loggedCustomer.segment_id);
    setCustomerSegmentName(loggedCustomer.segment_name);
    setCustomerProvider((_loggedCustomer$oauth = loggedCustomer.oauth_provider) !== null && _loggedCustomer$oauth !== void 0 ? _loggedCustomer$oauth : "visitor");
    setCustomerIdentificationNumber((_loggedCustomer$ident = loggedCustomer === null || loggedCustomer === void 0 ? void 0 : loggedCustomer.identification_number) !== null && _loggedCustomer$ident !== void 0 ? _loggedCustomer$ident : '');
    setCustomerBankUniqueId((_loggedCustomer$bank_ = loggedCustomer === null || loggedCustomer === void 0 ? void 0 : loggedCustomer.bank_uniq_id) !== null && _loggedCustomer$bank_ !== void 0 ? _loggedCustomer$bank_ : '');
  };
  const resetCustomerStates = () => {
    setCustomerIsLogged(customerDefaultValues.customerIsLogged);
    setCustomerId(customerDefaultValues.customerId);
    setCustomerName(customerDefaultValues.customerName);
    setCustomerLastName(customerDefaultValues.customerLastName);
    setCustomerEmail(customerDefaultValues.customerEmail);
    setCustomerProfilePic(customerDefaultValues.customerProfilePic);
    setCustomerBirthDate(customerDefaultValues.customerBirthDate);
    setCustomerGender(customerDefaultValues.customerGender);
    setCustomerMaritalStatus(customerDefaultValues.customerMaritalStatus);
    setCustomerCreditCardType(customerDefaultValues.customerCreditCardType);
    setCustomerPhoneNumber(customerDefaultValues.customerPhoneNumber);
    setCustomerCountry(customerDefaultValues.customerCountry);
    setCustomerState(customerDefaultValues.customerState);
    setCustomerCity(customerDefaultValues.customerCity);
    setCustomerPostalCode(customerDefaultValues.customerPostalCode);
    setCustomerFirstLine(customerDefaultValues.customerFirstLine);
    setCustomerAvailablePoints(customerDefaultValues.customerAvailablePoints);
    setCustomerCurrency(customerDefaultValues.customerCurrency);
    setCustomerDefaultCard(customerDefaultValues.customerDefaultCard);
    setCustomerSavedCreditCards(customerDefaultValues.customerSavedCreditCards);
    setCustomerIsValidated(customerDefaultValues.customerIsValidated);
    setCustomerSegmentId(customerDefaultValues.customer_segment_id);
    setCustomerSegmentName(customerDefaultValues.customer_segment_name);
    setCustomerProvider(customerDefaultValues.customerProvider);
    setIsLoadingCustomer(false);
    setIsSessionProviderActive(false);
    setShowUtilityModal(customerDefaultValues.showUtilityModal);
    setCustomerIdentificationNumber(customerDefaultValues.identification_number);
    setCustomerBankUniqueId(customerDefaultValues.bank_uniq_id);
  };
  const getAndSetSavedCreditCards = async () => {
    setIsLoadingCustomerSavedCreditCards(true);
    const getAndSetCreditCardLogo = async cardFlag => {
      const creditCardLogo = await assetsStotyblokService.getCreditCardBrandLogo(cardFlag);
      return creditCardLogo;
    };
    const savedCreditCards = await customersManager.fetchSavedCreditCards();

    // Fetch logos for each credit card
    const fetchLogos = async () => {
      const cardsWithLogos = await Promise.all(savedCreditCards.map(async creditCard => {
        const logoUrl = await getAndSetCreditCardLogo(creditCard.card_flag || "");
        return _objectSpread(_objectSpread({}, creditCard), {}, {
          card_flag: logoUrl !== null && logoUrl !== void 0 ? logoUrl : ""
        });
      }));
      return cardsWithLogos;
    };
    const cardsWithLogos = await fetchLogos();
    setCustomerSavedCreditCards(cardsWithLogos);
    cardsWithLogos.forEach(creditCard => {
      if (creditCard.is_default) {
        setCustomerDefaultCard(creditCard);
      }
    });
    setIsLoadingCustomerSavedCreditCards(false);
  };
  const addCreditCard = async creditCard => {
    const existingCardIndex = customerSavedCreditCards.findIndex(card => card.id === creditCard.id);
    const updatedCreditCards = customerSavedCreditCards.map((card, index) => _objectSpread(_objectSpread({}, card), {}, {
      is_default: index !== existingCardIndex ? false : card.is_default
    }));
    if (existingCardIndex !== -1) {
      updatedCreditCards[existingCardIndex] = _objectSpread(_objectSpread({}, creditCard), {}, {
        is_default: true
      });
      customersManager.updateDefaultFlag(+creditCard.id);
    } else {
      updatedCreditCards.unshift(creditCard);
    }
    setCustomerSavedCreditCards(updatedCreditCards);
  };
  const deleteCreditCard = async creditCard => {
    const customer = getLoggedCustomer();
    try {
      await customersManager.deleteCard(creditCard.id);
      const updatedCreditCards = customerSavedCreditCards.filter(card => card.id !== creditCard.id);
      setCustomerSavedCreditCards(updatedCreditCards);
      mixpanelService.trackEvent(MIXPANEL_EVENTS.CreditCard.CREDIT_CARD_DELETE, {
        Name: (customer === null || customer === void 0 ? void 0 : customer.first_name) + " " + (customer === null || customer === void 0 ? void 0 : customer.last_name),
        Email: customer === null || customer === void 0 ? void 0 : customer.email
      }, customer);
    } catch (error) {
      console.error('Failed to delete credit card:', error);
      throw error;
    }
  };
  const refreshAvailablePoints = async () => {
    setRefreshingPoints(true);
    const customer = await customersManager.getLoggedCustomer();
    if (customer) {
      setCustomerAvailablePoints(customer.muscle_points);
    }
    setRefreshingPoints(false);
  };
  const handleSearchRedirection = () => {
    var _process$env$NEXT_PUB, _localStorage$getItem2;
    const defaultApp = (_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_DEFAULT_APP) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : "hotels";
    const actualApp = (_localStorage$getItem2 = localStorage.getItem("current-app")) !== null && _localStorage$getItem2 !== void 0 ? _localStorage$getItem2 : defaultApp;
    const lastDataSearch = localStorage.getItem('lastSearch');
    let lastUserSearch;
    if (!lastDataSearch) {
      return router.push("/" + actualApp);
    }
    try {
      lastUserSearch = JSON.parse(lastDataSearch);
    } catch (error) {
      console.error('Invalid search data:', error);
      return;
    }
    if (lastUserSearch) {
      switch (actualApp) {
        case 'hotels':
          router.push('/hotels/search', {
            search: encodeObjectToQueryString(lastUserSearch)
          });
          break;
        case 'flights':
          router.push('/flights/select', {
            search: encodeObjectToQueryString(lastUserSearch)
          });
          break;
        case 'cars':
          router.push('/cars/search', {
            search: encodeObjectToQueryString(lastUserSearch)
          });
          break;
        default:
          break;
      }
    } else {
      router.push("/" + actualApp);
    }
  };
  const getLoggedCustomer = () => {
    const customer = {
      is_logged: customerIsLogged,
      id: customerId,
      email: customerEmail,
      first_name: customerName,
      last_name: customerLastName,
      muscle_points: customerAvailablePoints,
      currency: customerCurrency,
      first_line: customerFirstLine,
      city: customerCity,
      country: customerCountry,
      phone_number: customerPhoneNumber,
      date_of_birth: customerBirthDate,
      gender: customerGender,
      marital_status: customerMaritalStatus,
      state: customerState,
      postal_code: customerPostalCode,
      card_type: customerCreditCardType,
      profile_picture: customerProfilePic,
      segment_id: customerSegmentId,
      segment_name: customerSegmentName,
      profession: "",
      identification_number: customerIdentificationNumber,
      bank_unique_id: customerBankUniqueId
    };
    try {
      mixpanelService.identifyCustomer(customer);
    } catch (error) {
      console.error('Failed to identify customer:', error);
    }
    return customer;
  };
  return __jsx(CustomerContext.Provider, {
    value: {
      allowUnauthenticatedCustomer,
      isLoadingCustomer,
      isLoadingCustomerSavedCreditCards,
      login,
      loginRegisteredUser,
      logout,
      logoutFromFrontend,
      customerAvailablePoints,
      customerCurrencySymbol: "deprecated",
      // cannot delete yet, uses present in retail
      customerIsLogged,
      customerIsValidated,
      customerName,
      customerLastName,
      customerCurrency,
      customerProfilePic,
      customerEmail,
      customerBirthDate,
      customerGender,
      customerMaritalStatus,
      customerCreditCardType,
      customerPhoneNumber,
      customerCountry,
      customerState,
      customerCity,
      customerPostalCode,
      customerFirstLine,
      customerSavedCreditCards,
      customerDefaultCard,
      customerSegmentId,
      customerSegmentName,
      customerProvider,
      customerIdentificationNumber,
      customerBankUniqueId,
      addCreditCard,
      deleteCreditCard,
      updateCustomerStates,
      setCustomerAvailablePoints,
      setCustomerName,
      setCustomerLastName,
      setCustomerCurrency,
      setCustomerProfilePic,
      setCustomerEmail,
      initializeCustomerContext,
      getLoggedCustomer,
      getAndSetSavedCreditCards,
      isRefreshingData,
      setAuthenticatedCustomer,
      setCustomerIsValidated,
      checkActiveProviderSession,
      setCustomerIsLogged,
      refreshAvailablePoints,
      refreshingPoints,
      isSessionProviderActive: isSessionProviderActive,
      setIsSessionProviderActive,
      lastUserSearch,
      setLastUserSearch,
      isInitializingWithUrlRouteToken,
      showUtilityModal,
      setShowUtilityModal,
      handleSearchRedirection,
      showedCommunicationNotification,
      setShowedCommunicationNotification
    }
  }, children);
};
export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (context === null) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
};